import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { formatMoney } from '../../utils/formatNumber';
import { PeriodSelector } from './PeriodSelector';
import './AggregatedView.css';

/**
 * Компонент для отображения агрегированных данных за интервал
 */
import logger from "../../utils/logger";
export function AggregatedView({
  accountId
}) {
  const {
    request
  } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const loadAggregatedData = async range => {
    setLoading(true);
    setSelectedRange(range);
    try {
      // Загружаем данные за каждый месяц в интервале
      const months = generateMonthsRange(range.start, range.end);
      const monthlyData = [];
      for (const month of months) {
        try {
          const monthData = await request(`/api/v1/tax-reports/ozon/${accountId}/monthly/${month}`);
          monthlyData.push({
            period: month,
            ...monthData
          });
        } catch (error) {
          logger.warn(`Не удалось загрузить данные за ${month}:`, error);
        }
      }

      // Агрегируем данные
      const aggregated = aggregateMonthlyData(monthlyData);
      setData(aggregated);
    } catch (error) {
      logger.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };
  const aggregateMonthlyData = monthlyData => {
    const totals = {
      expenses: 0,
      income: 0,
      net: 0,
      creditPayments: 0,
      operations: 0,
      compensations: 0
    };
    const byMonth = monthlyData.map(month => {
      const monthExpenses = month.monthly?.expenses || 0;
      const monthIncome = month.monthly?.income || 0;
      const monthNet = month.monthly?.net || 0;
      const monthCreditPayments = month.monthly?.credit_payments || 0;
      totals.expenses += monthExpenses;
      totals.income += monthIncome;
      totals.net += monthNet;
      totals.creditPayments += monthCreditPayments;
      totals.operations += month.operations?.length || 0;
      totals.compensations += month.compensations?.length || 0;
      return {
        period: month.period,
        expenses: monthExpenses,
        income: monthIncome,
        net: monthNet,
        creditPayments: monthCreditPayments,
        operations: month.operations?.length || 0,
        compensations: month.compensations?.length || 0
      };
    });
    return {
      totals,
      byMonth
    };
  };
  const generateMonthsRange = (start, end) => {
    const months = [];
    const [startYear, startMonth] = start.split('-').map(Number);
    const [endYear, endMonth] = end.split('-').map(Number);
    let currentYear = startYear;
    let currentMonth = startMonth;
    while (currentYear < endYear || currentYear === endYear && currentMonth <= endMonth) {
      months.push(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    return months;
  };
  return <div className="aggregated-view">
      <h3>Агрегированный отчёт за интервал</h3>

      <PeriodSelector mode="range" onSelect={loadAggregatedData} />
      

      {loading && <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка данных...</p>
        </div>}

      {!loading && data && <>
          {/* Итого за интервал */}
          <div className="totals-card">
            <h4>
              Итого за период: {selectedRange?.start} – {selectedRange?.end}
            </h4>
            <div className="totals-grid">
              <div className="total-item total-income">
                <div className="total-label">Доходы</div>
                <div className="total-value">{formatMoney(data.totals.income)}</div>
              </div>
              <div className="total-item total-expenses">
                <div className="total-label">Расходы</div>
                <div className="total-value">{formatMoney(data.totals.expenses)}</div>
              </div>
              <div className="total-item total-net">
                <div className="total-label">Нетто</div>
                <div className="total-value">{formatMoney(data.totals.net)}</div>
              </div>
              <div className="total-item total-credits">
                <div className="total-label">Платежи по кредитам</div>
                <div className="total-value">{formatMoney(data.totals.creditPayments)}</div>
              </div>
            </div>

            <div className="totals-stats">
              <span>Операций: <strong>{data.totals.operations}</strong></span>
              <span>Компенсаций: <strong>{data.totals.compensations}</strong></span>
            </div>
          </div>

          {/* Детализация по месяцам */}
          <div className="monthly-breakdown">
            <h4>Детализация по месяцам</h4>
            <table className="monthly-table">
              <thead>
                <tr>
                  <th>Месяц</th>
                  <th className="align-right">Доходы</th>
                  <th className="align-right">Расходы</th>
                  <th className="align-right">Нетто</th>
                  <th className="align-right">Кредиты</th>
                  <th className="align-center">Операций</th>
                </tr>
              </thead>
              <tbody>
                {data.byMonth.map(month => <tr key={month.period}>
                    <td className="month-cell">{month.period}</td>
                    <td className="align-right income-cell">{formatMoney(month.income)}</td>
                    <td className="align-right expenses-cell">{formatMoney(month.expenses)}</td>
                    <td className={`align-right net-cell ${month.net >= 0 ? 'positive' : 'negative'}`}>
                      {formatMoney(month.net)}
                    </td>
                    <td className="align-right">{formatMoney(month.creditPayments)}</td>
                    <td className="align-center">{month.operations}</td>
                  </tr>)}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td><strong>ИТОГО:</strong></td>
                  <td className="align-right"><strong>{formatMoney(data.totals.income)}</strong></td>
                  <td className="align-right"><strong>{formatMoney(data.totals.expenses)}</strong></td>
                  <td className={`align-right ${data.totals.net >= 0 ? 'positive' : 'negative'}`}>
                    <strong>{formatMoney(data.totals.net)}</strong>
                  </td>
                  <td className="align-right"><strong>{formatMoney(data.totals.creditPayments)}</strong></td>
                  <td className="align-center"><strong>{data.totals.operations}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>}
    </div>;
}
